import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle } from "lucide-react";
import {
  buyersApi,
  type PrequalificationCandidateDto,
} from "@/api/buyers";
import {
  Button,
  EmptyState,
  Input,
  Skeleton,
  StatusBadge,
  Timestamp,
  useToast,
} from "@/design-system/components/ui";
// Modal is a staff-shell component, not part of the shared portal kit.
import { Modal } from "@/components/ui/Modal";
import { sizedClause } from "./prequalification/qualifiedAmount";
import { buyerKeys, buyerSubmissionKeys } from "@/hooks/buyers/buyerKeys";

/**
 * Pick the prequalification this buyer completed.
 *
 * 🔴 <b>Suggestions, confirmed one at a time — never an automatic link.</b> The
 * candidates are matched on email, which is a good signal and not a proof: two
 * people share an address, a spouse fills in a form, a buyer uses a work
 * address they later leave. So every row is a button and the specialist is the
 * one who decides.
 *
 * 🔴 <b>A submission another buyer already claims is called out loudly, not
 * footnoted.</b> Linking one is the specific mistake a confirm step exists to
 * prevent: it does not fail, it silently attributes one person's letter to
 * another, and the package would then carry the wrong buyer's prequalification
 * to an outside broker.
 */
export function PrequalificationCandidateModal({
  buyerLeadId,
  open,
  onClose,
}: {
  buyerLeadId: string;
  open: boolean;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const [search, setSearch] = useState("");

  // The server defaults the search to the buyer's own email, so an empty box is
  // the useful default rather than an empty result.
  const candidates = useQuery({
    queryKey: buyerKeys.prequalificationCandidates(buyerLeadId, search),
    queryFn: () => buyersApi.findPrequalificationCandidates(buyerLeadId, search || undefined),
    enabled: open && Boolean(buyerLeadId),
  });

  const link = useMutation({
    mutationFn: (submissionId: string) =>
      buyersApi.linkPrequalification(buyerLeadId, submissionId),
    onSuccess: () => {
      // 🔴 BOTH prefixes. Linking flips `canGenerate` on the SUBMISSION package
      // read, which lives under a different key entirely — invalidating only
      // the buyer would leave the Generate button disabled until staleTime
      // expired, with the specialist looking at a link that had worked.
      void qc.invalidateQueries({ queryKey: buyerKeys.all });
      void qc.invalidateQueries({ queryKey: buyerSubmissionKeys.all });
      toast.success("Prequalification linked.");
      onClose();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const rows = candidates.data ?? [];

  return (
    <Modal open={open} onClose={onClose} title="Link a prequalification" size="lg">
      <div className="grid gap-space-4">
        <div>
          <label className="block">
            <span className="text-caption uppercase tracking-wide text-gray-mid">
              Search by email
            </span>
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Leave blank to match this buyer's own email"
              data-testid="prequal-candidate-search"
              aria-label="Search prequalifications by email"
              className="mt-space-1"
            />
          </label>
          <p className="text-caption text-gray-mid mt-space-1 mb-0">
            Matches are suggestions, not proof of identity. Check the name and the
            date before linking.
          </p>
        </div>

        {candidates.isLoading && <Skeleton className="h-40 w-full" />}

        {!candidates.isLoading && rows.length === 0 && (
          <EmptyState
            title="No prequalifications found"
            description={
              search
                ? "No submission matches that address."
                : "Nothing matches this buyer's email address. Search for another one, or send the buyer through the prequalification funnel."
            }
          />
        )}

        <div className="grid gap-space-3">
          {rows.map((c) => (
            <CandidateRow
              key={c.submissionId}
              candidate={c}
              disabled={link.isPending}
              onLink={() => link.mutate(c.submissionId)}
            />
          ))}
        </div>
      </div>
    </Modal>
  );
}

function CandidateRow({
  candidate: c,
  disabled,
  onLink,
}: {
  candidate: PrequalificationCandidateDto;
  disabled: boolean;
  onLink: () => void;
}) {
  const claimed = Boolean(c.alreadyLinkedToBuyerId);

  return (
    <div
      className="border border-border-light rounded-md p-space-3 bg-white-pure"
      data-testid="prequal-candidate-row"
    >
      <div className="flex flex-wrap items-start justify-between gap-space-3">
        <div className="min-w-0">
          <div className="text-small font-medium text-onyx">{c.fullName}</div>
          <div className="text-caption text-gray-mid">
            {c.email}
            {c.referenceNumber ? ` · ${c.referenceNumber}` : ""}
          </div>
          <div className="text-caption text-gray-mid mt-space-1">
            Submitted <Timestamp value={c.submittedAtUtc} />
            {sizedClause(c)}
          </div>
        </div>

        <div className="flex flex-col items-end gap-space-2 shrink-0">
          <div className="flex items-center gap-space-2">
            <StatusBadge tone={c.outcome === "Pass" ? "ok" : "neutral"}>
              {c.outcome}
            </StatusBadge>
            {/*
              Shown rather than hidden: linking a prequalification with no letter
              leaves the package still blocked, and the specialist should know
              that before clicking rather than after.
            */}
            <StatusBadge tone={c.hasLetter ? "ok" : "warn"}>
              {c.hasLetter ? "Letter on file" : "No letter"}
            </StatusBadge>
          </div>
          {/* `sm`, like every other control on the prequalification surfaces.
              It rendered a size larger than everything behind it — including the
              Send-invitation button that is the module's own primary act —
              because the size prop was left off and the component's default
              filled it in, which reads as emphasis nobody chose. It is one
              choice among N candidate rows, so it takes the SECONDARY weight
              rather than the primary one. */}
          <Button
            variant="secondary"
            size="sm"
            onClick={onLink}
            disabled={disabled}
            data-testid="prequal-candidate-link"
          >
            Link this one
          </Button>
        </div>
      </div>

      {claimed && (
        <p
          className="flex items-start gap-space-1 text-small text-status-warning mt-space-3 mb-0"
          role="alert"
          data-testid="prequal-candidate-claimed"
        >
          <AlertTriangle size={14} aria-hidden className="mt-[2px] shrink-0" />
          <span>
            Already linked to {c.alreadyLinkedToBuyerName}. Linking it here would
            attribute their prequalification to this buyer — check you have the
            right person first.
          </span>
        </p>
      )}
    </div>
  );
}
